/**
 * Delete Role Confirmation Dialog
 *
 * AlertDialog component for confirming role deletion.
 * Displays warning message and destructive action button.
 *
 * @module domains/personnel/presentation/components/shared/DeleteRoleDialog
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@parrot-software/design-system';

/**
 * Props for DeleteRoleDialog component
 */
export interface DeleteRoleDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Role name to display in confirmation message */
  roleName: string;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Whether deletion is in progress */
  isLoading?: boolean;
}

/**
 * Delete Role Confirmation Dialog Component
 *
 * Displays a confirmation dialog before deleting a role.
 * Shows warning message and requires explicit confirmation.
 *
 * @param {DeleteRoleDialogProps} props - Component properties
 * @returns {React.ReactElement} Delete confirmation dialog
 *
 * @example
 * const [isOpen, setIsOpen] = useState(false);
 * const { deleteRole, isLoading } = useDeleteRole();
 *
 * <DeleteRoleDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   roleName="Gerente"
 *   onConfirm={() => deleteRole(roleUuid)}
 *   isLoading={isLoading}
 * />
 */
export const DeleteRoleDialog = ({
  open,
  onOpenChange,
  roleName,
  onConfirm,
  isLoading = false,
}: DeleteRoleDialogProps): React.ReactElement => {
  const { t } = useTranslation('roles');

  const handleConfirm = () => {
    onConfirm();
    // Keep dialog open until deletion completes
    // The parent component should close it after successful deletion
  };

  return (
    <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent className="max-w-[451px] font-sans">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('confirmDelete.title')}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-1">
            <p className="text-muted-foreground">{t('confirmDelete.description')}</p>
            <p className="font-semibold text-muted-foreground">{t('confirmDelete.warning')}</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>{t('confirmDelete.cancelButton')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            disabled={isLoading}
            onClick={handleConfirm}
          >
            {isLoading ? t('confirmDelete.loadingButton') : t('confirmDelete.confirmButton')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
